import { Column, Row } from "@stenajs-webui/core";
import { FlatButton, stenaPlusCircle } from "@stenajs-webui/elements";
import { RadioButtonWithLabel } from "@stenajs-webui/forms";
import { isEmpty, map } from "lodash";
import type { FC } from "react";
import { useDispatch, useSelector } from "react-redux";

import { DetailsFieldCell } from "../../../../common-ui/details/DetailsFieldCell";
import { trackEvent } from "../../../../common/tracking/trackerService";
import { getGuid } from "../../../../common/utils/guidUtils";
import { transformYesNoOption } from "../../../../common/utils/optionTransformers";
import {
  addCreateIntermodalBookingGood,
  clearCreateBookingGoods,
  getCreateIntermodalBookingIsSubmitting,
} from "../../../../services/intermodal/createIntermodalReducer";
import { useCreateIntermodalFieldValue } from "../../../../services/intermodal/hooks/UseCreateIntermodalFieldValue";
import type { GoodsTypesQueryRefProps } from "../../../../services/intermodal/hooks/useGoodsTypes";
import {
  pluginsRange,
  yesNoOptions,
} from "../../../manage/details/components/sections/common";
import { transformNoOfPluginsOption } from "../../utils/detailsUtils";
import { CreateBookingGoodsItem } from "../CreateBookingGoodsItem";
import { CreateIntermodalDropdownField } from "../fields/CreateIntermodalDropdownField";
import { CreateIntermodalTextInputField } from "../fields/CreateIntermodalTextInputField";

export const CreateIntermodalCargoSection: FC<GoodsTypesQueryRefProps> = ({
  goodsTypesQueryRef,
}) => {
  const bookingGoods = useCreateIntermodalFieldValue("bookingGoods");
  const dispatch = useDispatch();
  const disabled = useSelector(getCreateIntermodalBookingIsSubmitting);
  const loaded = !isEmpty(bookingGoods);

  const clearGoods = () => dispatch(clearCreateBookingGoods());
  const addGood = () =>
    dispatch(addCreateIntermodalBookingGood({ goodId: getGuid() }));

  return (
    <>
      <DetailsFieldCell
        label={"Hazardous goods"}
        renderField={(id) => (
          <CreateIntermodalDropdownField
            id={id}
            trackerCategory={"IntermodalCreate"}
            transformOption={transformYesNoOption}
            name={"hazardousGoods"}
            options={yesNoOptions}
          />
        )}
      />
      <Row gap={2}>
        <DetailsFieldCell
          label={"Plugin"}
          renderField={(id) => (
            <CreateIntermodalDropdownField
              id={id}
              trackerCategory={"IntermodalCreate"}
              transformOption={transformNoOfPluginsOption}
              options={pluginsRange}
              name={"noOfPlugins"}
            />
          )}
          flex={1}
        />
        <DetailsFieldCell
          label={"Temp (°C)"}
          renderField={(id) => (
            <CreateIntermodalTextInputField
              id={id}
              trackerCategory={"IntermodalCreate"}
              name={"temperature"}
            />
          )}
          flex={1}
        />
      </Row>
      <Column gap>
        <RadioButtonWithLabel
          name={"loaded"}
          label={"Empty"}
          disabled={disabled}
          checked={!loaded}
          onClick={loaded ? clearGoods : undefined}
        />
        <RadioButtonWithLabel
          name={"loaded"}
          label={"Loaded"}
          disabled={disabled}
          onClick={!loaded ? addGood : undefined}
          checked={loaded}
        />
      </Column>
      {loaded && (
        <>
          <Column gap={3}>
            {map(bookingGoods, (good) => (
              <CreateBookingGoodsItem
                key={good.id}
                value={good}
                editable={!disabled}
                goodsTypesQueryRef={goodsTypesQueryRef}
              />
            ))}
          </Column>
          <FlatButton
            leftIcon={stenaPlusCircle}
            disabled={disabled}
            label="Add goods"
            onClick={() => {
              trackEvent({ category: "IntermodalCreate", action: "Add goods" });
              addGood();
            }}
          />
        </>
      )}
    </>
  );
};
